import React, { useContext, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client';

import {
  SEARCH_MODELS_QUERY,
  transformModelResponse,
} from '../../utils/model_server';
import { modelsIndexPollInterval } from '../../config';
import useWorkspaceContext from '../workspaces/WorkspaceContext/WorkspaceContext';

const ModelSearchContext = React.createContext({
  error: null,
  loading: false,
  models: [],
  searchQuery: '',
  setSearchQuery: () => {},
});

const useModelSearchQuery = (query = '', workspaceId = '') => {
  const [searchQuery, setSearchQuery] = useState(query);

  const wildcardAugmentedSearchQuery = `%${searchQuery.toLowerCase()}%`;

  const searchModelsQueryResult = useQuery(SEARCH_MODELS_QUERY, {
    skip: !workspaceId,
    variables: {
      query: wildcardAugmentedSearchQuery,
      workspace_id: workspaceId,
    },
    fetchPolicy: 'cache-and-network',
    pollInterval: modelsIndexPollInterval,
  });

  const models
    = !!searchModelsQueryResult.data?.search_workspace_models && !searchModelsQueryResult.error
      ? searchModelsQueryResult.data.search_workspace_models.map(transformModelResponse)
      : null;

  if (searchModelsQueryResult.error) {
    console.error('got error', searchModelsQueryResult.error);
  }
  
  return {
    models,
    loading: searchModelsQueryResult?.loading,
    error: searchModelsQueryResult?.error,
    searchQuery,
    setSearchQuery,
  }
}

export const ModelSearchProvider = ({ children, query = '' }) => {
  const { currentWorkspaceId } = useWorkspaceContext();
  const {
    models,
    loading,
    error,
    searchQuery,
    setSearchQuery
  } = useModelSearchQuery(query, currentWorkspaceId);
  
  const modelSearchContext = useMemo(() => ({
    models,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  }), [
    models,
    loading,
    error,
    searchQuery,
    setSearchQuery,
  ])

  return (
    <ModelSearchContext.Provider
      value={modelSearchContext}
    >
      { children }
    </ModelSearchContext.Provider>
  )
}

export const useModelSearch = () => useContext(ModelSearchContext);

export default useModelSearch;
